/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback } from "react";
import { isPointWithinRadius } from "geolib";
import { t } from "i18next";
import { ToastClose } from "@/components/ui/toast";
import { useToast, setLimit } from "@/components/ui/use-toast";
import useMapStore from "@/store/mapStore";
import { StopModel } from "@/models/stop.model";
import { getWayfindingConfig } from "@/lib/utils";

export default function UseRouting() {
  const map = useMapStore((state) => state.map);
  const accessibleRoute = useMapStore((state) => state.accessibleRoute);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const cameraRecognitionFeature = useMapStore(
    (state) => state.cameraRecognitionFeature,
  );
  const { toast } = useToast();
  setLimit(1);

  const findRoute = useCallback(
    ({
      start,
      finish,
      stops,
      type,
      preview,
    }: {
      start?: string;
      finish: string;
      stops?: StopModel[];
      type?: "closest";
      preview?: boolean;
    }) => {
      if (gpsMode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const gpsControl: any = map
          .getMapboxInstance()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ._controls.find((c: any) => c._lastKnownPosition);

        const isInside = gpsControl
          ? isPointWithinRadius(
              {
                lat: gpsControl._lastKnownPosition.coords.latitude,
                lng: gpsControl._lastKnownPosition.coords.longitude,
              },
              {
                lat: +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LATITUDE,
                lng: +import.meta.env
                  .VITE_WAYFINDING_DEFAULT_LOCATION_LONGITUDE,
              },
              6000,
            )
          : false;

        if (isInside) {
          if (gpsControl._lastKnownPosition.coords.heading) {
            map.setInitialBearing(gpsControl._lastKnownPosition.coords.heading);
          }
        } else {
          // map.enableRouteAnimation();
          start =
            stops && stops.length > 1
              ? stops[0].feature!.id
              : import.meta.env.VITE_WAYFINDING_ENTRANCE_FEATURE_ID;
          if (stops?.length === 0 || !stops) {
            toast({
              duration: 8000,
              title: t("routeFailedTitle"),
              description: t("routeFailed"),
              variant: "primary",
              action: (
                <ToastClose aria-label="Close">
                  <span aria-hidden>×</span>
                </ToastClose>
              ),
            });
          }
        }
      }

      if (activeKiosk?.bearing) {
        map.setInitialBearing(activeKiosk.bearing);
      }

      if (cameraRecognitionFeature?.id) {
        start = cameraRecognitionFeature.id;
        if (cameraRecognitionFeature.properties.bearing) {
          map.setInitialBearing(cameraRecognitionFeature.properties.bearing);
        }
      }

      if (type === "closest") {
        console.log("closest", finish, start);
        map.findRouteToNearestFeature(
          finish,
          start,
          accessibleRoute,
          getWayfindingConfig(accessibleRoute),
        );
        return;
      }

      if (finish && (stops?.length === 0 || !stops)) {
        map.findRouteByIds(
          finish,
          start,
          accessibleRoute,
          getWayfindingConfig(accessibleRoute),
          !preview,
        );
        map.setNavStep(0);
        console.log("findRoute", {
          start,
          finish,
          preview: !preview,
        });
      } else {
        if (start) {
          if (stops?.length === 2) {
            map.findRouteByIds(
              finish,
              cameraRecognitionFeature?.id
                ? cameraRecognitionFeature.id
                : stops[0].feature!.id,
              accessibleRoute,
              getWayfindingConfig(accessibleRoute),
              !preview,
            );
          } else {
            map.findMultipointRoute({
              start,
              stops: stops!
                .filter((s) => s.feature)
                .slice(1)
                .map((s) => s.feature!.id), // remove first stop as it's used as a start
              wayfindingConfig: getWayfindingConfig(accessibleRoute),
              autoStart: !preview,
            });
          }

          console.log("find route", {
            start,
            finish,
            stops: stops?.slice(1).map((s) => s.feature?.id),
            autoStart: !preview,
          });
        }
      }
    },
    [
      accessibleRoute,
      map,
      gpsMode,
      toast,
      cameraRecognitionFeature,
      activeKiosk,
    ],
  );

  return [findRoute];
}
